/**
 * screens/candidates/candidatesList.tsx
 * Candidates list — wired to GET /api/hire/v2/candidates. Premium table with
 * staggered entrance + hover rows and per-row actions:
 *   - Edit   → "Update resume" (append a new version → re-extract profile). PII
 *              is immutable by design, so this is the sanctioned candidate edit.
 *   - Delete → Withdraw consent (DPDP, append-only): the candidate stops being
 *              matched; the record is kept. Confirm + success dialogs.
 * All writes route through the service layer + centralized toast/dialog stack.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { useNavigation, useFocusEffect, useIsFocused } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useTheme } from '../../context/themeContext';
import { useBreakpoints, ContentMaxWidth } from '../../theme/responsive';
import { web } from '../../utils/web';
import { useHover } from '../../hooks/useHover';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useWorkspaceStats } from '../../hooks/useAiUsage';
import { ActionButton, Panel, Icon, CommonSearch, StatChip, Pagination, EmptyState } from '../../components/ui';
import { MiniAvatar } from '../../components/hire/badges';
import ConfirmDialog from '../../components/confirmDialog';
import SuccessDialog from '../../components/successDialog';
import { showToast } from '../../components/toastComponent';
import { getApiErrorMessage } from '../../utils/errorHandler';
import AddCandidateV2Modal from '../../components/hire/addCandidateV2Modal';
// "Bulk import" → many resume files in one pass (extract + create per file).
import BulkUploadModal from '../../components/hire/bulkUploadModal';
import EditCandidatePiiModal from '../../components/hire/editCandidatePiiModal';
import { listCandidates, getCandidate, recordConsent } from '../../services/hire/candidateV2';
import type { CandidateListItem, CandidateListResult, CandidateDetail } from '../../schemas/hire/candidateV2';
import type { AppStackParamList } from '../../navigation/types';

type Nav = NativeStackNavigationProp<AppStackParamList>;

/** Premium staggered entrance (web-only; inert on native). */
function rise(delay: number) {
  return web({ animation: 'sk-rise 460ms cubic-bezier(.2,.7,.2,1) both', animationDelay: `${delay}ms` });
}

// Distinct accent hues for row actions (the only colour in the grayscale system).
const ICON_TONES = {
  edit:   { active: '#3B6FE0', soft: 'rgba(59,111,224,0.14)', ring: 'rgba(59,111,224,0.55)', glow: 'rgba(59,111,224,0.45)' },
  delete: { active: '#E04848', soft: 'rgba(224,72,72,0.14)',  ring: 'rgba(224,72,72,0.55)',  glow: 'rgba(224,72,72,0.45)' },
} as const;

function IconBtn({
  name, label, kind, loading, onPress,
}: { name: string; label: string; kind: keyof typeof ICON_TONES; loading?: boolean; onPress: () => void }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const { hovered, hoverProps } = useHover();
  const t = ICON_TONES[kind];
  return (
    <Pressable
      {...hoverProps}
      onPress={onPress}
      accessibilityLabel={label}
      disabled={loading}
      style={[
        {
          width: 34, height: 34, alignItems: 'center', justifyContent: 'center', borderRadius: 10, borderWidth: 1,
          borderColor: hovered ? t.ring : colors.line,
          backgroundColor: hovered ? t.soft : colors.paper2,
        },
        web({ cursor: 'pointer', transition: 'transform 170ms cubic-bezier(.2,.7,.2,1), background 170ms ease, border-color 170ms ease, box-shadow 170ms ease' }),
        hovered && web({ transform: 'translateY(-2px) scale(1.05)', boxShadow: `0 8px 18px -9px ${t.glow}` }),
      ]}
    >
      {loading ? <ActivityIndicator size="small" color={t.active} /> : <Icon name={name} size={15} color={hovered ? t.active : colors.ink3} />}
    </Pressable>
  );
}

function HeaderCell({ label }: { label: string }) {
  const { theme } = useTheme();
  const { colors, fontWeight } = theme;
  return <Text numberOfLines={1} style={{ flex: 1, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', color: colors.ink3, fontWeight: fontWeight.semibold }}>{label}</Text>;
}

/**
 * SINGLE source of truth for the table's column geometry — header and rows read
 * the same spec so they can never drift apart. Tablet (820–1079) gets a
 * condensed variant: tighter gutters, a narrower S.NO, and no Source column
 * (it's low-signal and was the column the identity cell used to spill into).
 */
function useColumns() {
  const { isTablet } = useBreakpoints();
  return isTablet
    ? { gap: 8, padH: 12, sno: 30, name: 3.55, source: 0, location: 1, exp: 0.6, resume: 0.6, actions: 78 }
    : { gap: 12, padH: 16, sno: 44, name: 3.9, source: 0.9, location: 1.05, exp: 0.6, resume: 0.65, actions: 82 };
}

/** One label/value pair in the mobile card's meta grid. */
function MetaField({ label, children }: { label: string; children: React.ReactNode }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  return (
    <View style={{ minWidth: 0 }}>
      <Text style={{ fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', color: colors.ink4, fontWeight: fontWeight.semibold, marginBottom: 2 }}>{label}</Text>
      {typeof children === 'string' ? (
        <Text numberOfLines={1} style={{ fontSize: fontSize.sm, color: colors.ink2 }}>{children}</Text>
      ) : children}
    </View>
  );
}

function Row({
  item, index, pageOffset = 0, onOpen, onEdit, onDelete, deleting, editLoading,
}: {
  item: CandidateListItem; index: number; pageOffset?: number;
  onOpen: () => void; onEdit: () => void; onDelete: () => void; deleting: boolean; editLoading: boolean;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const { isMobile } = useBreakpoints();
  const col = useColumns();

  // Mobile: a premium stacked card — identity header, hairline, then an aligned
  // meta grid (Source/Location on one row; Exp/Resume on the next).
  if (isMobile) {
    return (
      <View
        {...hoverProps}
        style={[
          { padding: 16, borderRadius: radii.lg, borderWidth: 1, borderColor: hovered ? colors.lineStrong : colors.line, backgroundColor: colors.card, gap: 14 },
          rise(60 + index * 45),
          web({ transition: 'border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease' }),
          Platform.OS === 'web' ? ({ boxShadow: hovered ? '0 12px 28px -16px rgba(0,0,0,0.35)' : '0 1px 0 rgba(16,24,40,0.04)' } as object) : null,
          hovered ? web({ transform: 'translateY(-2px)' }) : null,
        ]}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11 }}>
          <Pressable onPress={onOpen} style={[{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 11, minWidth: 0 }, web({ cursor: 'pointer' })]}>
            <MiniAvatar name={item.name} size={42} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text numberOfLines={1} style={{ fontSize: fontSize.lg, color: colors.ink, fontWeight: fontWeight.semibold, letterSpacing: -0.2 }}>{item.name}</Text>
              {item.possibleDuplicateOfCode ? (
                <Text numberOfLines={2} style={{ fontSize: fontSize.xs, color: colors.warn, fontWeight: fontWeight.medium, marginTop: 2, lineHeight: fontSize.xs * 1.45 }}>
                  ⧉ same as {item.possibleDuplicateOfCode}  ·  {item.title ?? '—'}
                </Text>
              ) : (
                <Text numberOfLines={2} style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 2, lineHeight: fontSize.xs * 1.45 }}>{item.title ?? '—'}</Text>
              )}
            </View>
          </Pressable>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <IconBtn name="edit" label="Edit contact details" kind="edit" loading={editLoading} onPress={onEdit} />
            <IconBtn name="trash" label="Withdraw consent" kind="delete" loading={deleting} onPress={onDelete} />
          </View>
        </View>
        <View style={{ height: 1, backgroundColor: colors.line }} />
        {/* Both rows share the SAME 3-column grid so every column's left edge
            lines up (Source↔Exp, Location↔Resume). */}
        <View style={{ gap: 16 }}>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <View style={{ flex: 1, minWidth: 0 }}><MetaField label="Source">{item.source}</MetaField></View>
            <View style={{ flex: 1, minWidth: 0 }}><MetaField label="Location">{item.location ?? '—'}</MetaField></View>
            <View style={{ flex: 1, minWidth: 0 }} />
          </View>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <MetaField label="Exp"><Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink2 }}>{item.experienceYears != null ? `${item.experienceYears}y` : '—'}</Text></MetaField>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <MetaField label="Resume"><Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink2 }}>v{item.resumeVersions}</Text></MetaField>
            </View>
            <View style={{ flex: 1, minWidth: 0 }} />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View
      {...hoverProps}
      style={[
        { flexDirection: 'row', alignItems: 'center', gap: col.gap, paddingHorizontal: col.padH, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: hovered ? colors.paper2 : 'transparent' },
        web({ transition: 'background 120ms ease' }),
        rise(60 + index * 45),
      ]}
    >
      <Pressable onPress={onOpen} style={[{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: col.gap, minWidth: 0 }, web({ cursor: 'pointer' })]}>
        <Text style={{ width: col.sno, fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink4, fontWeight: fontWeight.semibold }}>{pageOffset + index + 1}</Text>
        <View style={{ flex: col.name, flexDirection: 'row', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <MiniAvatar name={item.name} size={32} />
          {/* flex:1 + minWidth:0 — without it a long title/duplicate note grows
              the cell past its flex basis and spills into the next column. */}
          <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
            <Text numberOfLines={1} style={{ fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.semibold, lineHeight: fontSize.md * 1.3 }}>{item.name}</Text>
            {/* A flagged repeat is listed directly under the record it repeats
                (grouped server-side), so name the link here. The note carries two
                facts (the linked code + the title), so it wraps onto a second
                line rather than truncating either of them away. */}
            {item.possibleDuplicateOfCode ? (
              <Text numberOfLines={2} style={{ fontSize: fontSize.xs, color: colors.warn, fontWeight: fontWeight.medium, lineHeight: fontSize.xs * 1.45 }}>
                ⧉ same as {item.possibleDuplicateOfCode}  ·  {item.title ?? '—'}
              </Text>
            ) : (
              <Text numberOfLines={2} style={{ fontSize: fontSize.xs, color: colors.ink3, lineHeight: fontSize.xs * 1.45 }}>{item.title ?? '—'}</Text>
            )}
          </View>
        </View>
        {col.source > 0 && (
          <Text numberOfLines={1} style={{ flex: col.source, fontSize: fontSize.sm, color: colors.ink2 }}>{item.source}</Text>
        )}
        <Text style={{ flex: col.location, fontSize: fontSize.sm, color: colors.ink2 }} numberOfLines={1}>{item.location ?? '—'}</Text>
        <Text numberOfLines={1} style={{ flex: col.exp, fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink2 }}>{item.experienceYears != null ? `${item.experienceYears}y` : '—'}</Text>
        <Text numberOfLines={1} style={{ flex: col.resume, fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink2 }}>v{item.resumeVersions}</Text>
      </Pressable>
      <View style={[{ width: col.actions, flexDirection: 'row', gap: 6, justifyContent: 'flex-end' }, web({ opacity: hovered ? 1 : 0.72, transition: 'opacity 140ms ease' })]}>
        <IconBtn name="edit" label="Edit contact details" kind="edit" loading={editLoading} onPress={onEdit} />
        <IconBtn name="trash" label="Withdraw consent" kind="delete" loading={deleting} onPress={onDelete} />
      </View>
    </View>
  );
}

export default function CandidatesListScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const { isDark } = useTheme();
  const { isMobile, isTablet } = useBreakpoints();
  const col = useColumns();
  const navigation = useNavigation<Nav>();

  const [result, setResult] = useState<CandidateListResult | null>(null);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 250);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState<CandidateDetail | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CandidateListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [withdrawn, setWithdrawn] = useState<string | null>(null); // success dialog (candidate name)

  const PAGE_SIZE = 10;

  // Search + pagination run SERVER-SIDE; the page + aggregate counts come back in
  // one response. A per-request id + abort drops stale pages while typing/paging.
  const reqIdRef = useRef(0);
  const fetchList = useCallback((q: string, pg: number) => {
    const myReq = ++reqIdRef.current;
    const controller = new AbortController();
    listCandidates({ q, page: pg, pageSize: PAGE_SIZE }, controller.signal)
      .then((r) => { if (myReq === reqIdRef.current) setResult(r); })
      .catch(() => {
        if (myReq === reqIdRef.current && !controller.signal.aborted) {
          setResult({ items: [], page: 1, pageSize: PAGE_SIZE, total: 0, counts: { total: 0, active: 0, consent_expired: 0 } });
        }
      });
    return () => controller.abort();
  }, []);

  // One callback drives initial load, live search, page changes and focus.
  const refresh = useCallback(() => fetchList(debouncedQuery, page), [fetchList, debouncedQuery, page]);
  useFocusEffect(refresh);

  const onSearch = (t: string) => { setQuery(t); setPage(1); };

  // Live: refetch when something changes elsewhere (SSE rev bump), DEBOUNCED so a
  // burst of changes (or our own mutations) collapses into a single refetch
  // instead of one call per bump.
  //
  // Two guards keep this from stampeding the API:
  //  1. isFocused — native-stack keeps prior screens MOUNTED underneath the top
  //     one, so an unguarded rev effect makes every mounted list refetch on the
  //     same bump. We only refetch the visible screen; useFocusEffect(refresh)
  //     above catches a backgrounded screen up the moment it's shown again.
  //  2. refresh via ref — reading refresh from a ref (instead of an effect dep)
  //     means a query/page change no longer re-runs this effect and clears the
  //     pending debounce timer, so bursts actually coalesce.
  const isFocused = useIsFocused();
  const { rev } = useWorkspaceStats();
  const lastRev = useRef(rev);
  const revTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    // While backgrounded, absorb bumps so useFocusEffect(refresh) owns the
    // single catch-up fetch on refocus (no duplicate refetch here).
    if (!isFocused) { lastRev.current = rev; return; }
    if (rev === lastRev.current) return;
    lastRev.current = rev;
    if (revTimer.current) clearTimeout(revTimer.current);
    // 1.2s, not 300ms: one user action bumps `rev` several times server-side
    // (record + consent + derived profile + AI spend), and at 300ms each bump
    // landed as its own list fetch.
    revTimer.current = setTimeout(() => refreshRef.current(), 1_200);
    return () => { if (revTimer.current) clearTimeout(revTimer.current); };
  }, [rev, isFocused]);

  const openDetail = (id: string) => navigation.navigate('candidateDetail', { id });
  // Navigates straight to the new candidate; the list refetches on focus return.
  const onDone = (c: CandidateDetail) => { setModalOpen(false); openDetail(c.id); };

  // Edit → fetch the full record (email/phone) then open the contact-detail form.
  const onEditClick = async (item: CandidateListItem) => {
    setEditLoadingId(item.id);
    try {
      setEditing(await getCandidate(item.id));
    } catch (e) {
      showToast('error', 'Could not open the candidate', getApiErrorMessage(e));
    } finally {
      setEditLoadingId(null);
    }
  };

  const doWithdraw = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setDeleting(true);
    // Optimistic: drop the row immediately so it never flickers back. The
    // backend deactivates the record (consent trail preserved) and the list
    // excludes it, so the debounced SSE reconcile keeps it gone.
    setResult((prev) => (prev ? { ...prev, items: prev.items.filter((c) => c.id !== target.id) } : prev));
    try {
      await recordConsent(target.id, 'withdraw');
      setPendingDelete(null);
      setWithdrawn(target.name);
    } catch (e) {
      showToast('error', 'Could not withdraw consent', getApiErrorMessage(e));
      refresh(); // failed → restore the true list
    } finally {
      setDeleting(false);
    }
  };

  const loading = result === null;
  const shown = result?.items ?? [];
  const counts = result?.counts ?? { total: 0, active: 0, consent_expired: 0 };
  const total = result?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const searching = debouncedQuery.trim().length > 0;
  const hasAny = counts.total > 0;

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : isTablet ? 22 : 32, paddingBottom: 72 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 18 }}>
        {isMobile ? (
          // Mobile: the title owns its line and the two actions sit on the next,
          // each taking half the width — side-by-side they overflow a phone.
          <View style={[{ gap: 12 }, rise(20)]}>
            <Text style={{ fontSize: 22, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.5 }}>Candidates</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <View style={{ flexGrow: 1, flexBasis: 0, minWidth: 0 }}>
                <ActionButton label="Bulk import" variant="secondary" icon="upload" onPress={() => setBulkOpen(true)} />
              </View>
              <View style={{ flexGrow: 1, flexBasis: 0, minWidth: 0 }}>
                <ActionButton label="Add candidate" variant="primary" icon="upload" onPress={() => setModalOpen(true)} />
              </View>
            </View>
          </View>
        ) : (
          <View style={[styles.header, { alignItems: 'center', gap: 12, flexWrap: 'wrap' }, rise(20)]}>
            <Text style={{ fontSize: 22, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.5 }}>Candidates</Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <ActionButton label="Bulk import" variant="secondary" icon="upload" onPress={() => setBulkOpen(true)} />
              <ActionButton label="Add candidate" variant="primary" icon="upload" onPress={() => setModalOpen(true)} />
            </View>
          </View>
        )}

        {/* Premium totals — the pool snapshot at a glance. Hidden while searching
            (the search field shows its own live match count). */}
        {!loading && !searching && hasAny && (
          <View style={[{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }, rise(28)]}>
            <View style={isMobile ? { flexGrow: 1, flexBasis: 0, minWidth: 0 } : undefined}>
              <StatChip value={counts.total} label="Total candidates" emphasis />
            </View>
            <View style={isMobile ? { flexGrow: 1, flexBasis: 0, minWidth: 0 } : undefined}>
              <StatChip value={counts.active} label="Active" tone="success" />
            </View>
            {counts.consent_expired > 0 && (
              <View style={isMobile ? { flexBasis: '100%' } : undefined}>
                <StatChip value={counts.consent_expired} label="Consent expired" tone="warn" />
              </View>
            )}
          </View>
        )}

        {/* Search (left) + pagination (right) on one row. Search stays visible
            during a zero-result search so the term can be cleared. */}
        {!loading && (hasAny || searching) && (
          <View style={[isMobile ? { gap: 14 } : { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }, rise(40)]}>
            <View style={isMobile ? { width: '100%' } : { flex: 1, minWidth: 220, maxWidth: 440 }}>
              <CommonSearch
                value={query}
                onChange={onSearch}
                isDark={isDark}
                placeholder="Search candidates by name, title or location…"
                count={searching ? counts.total : null}
                countNoun="match"
              />
            </View>
            <View style={isMobile ? { flexDirection: 'row', justifyContent: 'flex-end' } : undefined}>
              <Pagination page={result?.page ?? 1} pageCount={pageCount} onChange={setPage} />
            </View>
          </View>
        )}

        {loading ? (
          <View style={{ paddingVertical: 60 }}><ActivityIndicator size="large" color={colors.accent} /></View>
        ) : !hasAny && !searching ? (
          <EmptyState
            icon="users"
            title="No candidates yet"
            subtitle="Add a candidate to build your candidate pool — the AI extracts skills, experience and contact details in one pass."
            action={{ label: 'Add candidate', icon: 'upload', variant: 'primary', onPress: () => setModalOpen(true) }}
          />
        ) : shown.length === 0 ? (
          <EmptyState
            icon="search"
            title={`No candidates match “${debouncedQuery.trim()}”`}
            subtitle="Try a different name, skill or location — or clear the search to see the whole pool."
            action={{ label: 'Clear search', icon: 'x', variant: 'secondary', onPress: () => onSearch('') }}
          />
        ) : (
          <View style={rise(60)}>
            {isMobile ? (
              // Mobile: a gapped column of stacked cards — the table's fixed
              // columns can't fit at phone widths, so each row reflows.
              <View style={{ gap: 12 }}>
                {shown.map((it, i) => (
                  <Row
                    key={it.id}
                    item={it}
                    index={i}
                    pageOffset={((result?.page ?? 1) - 1) * PAGE_SIZE}
                    deleting={deleting && pendingDelete?.id === it.id}
                    editLoading={editLoadingId === it.id}
                    onOpen={() => openDetail(it.id)}
                    onEdit={() => onEditClick(it)}
                    onDelete={() => setPendingDelete(it)}
                  />
                ))}
              </View>
            ) : (
              <Panel flush>
                <View style={{ flexDirection: 'row', gap: col.gap, paddingHorizontal: col.padH, paddingVertical: 11, backgroundColor: colors.paper2, borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
                  <View style={{ flex: 1, flexDirection: 'row', gap: col.gap, minWidth: 0 }}>
                    <View style={{ width: col.sno }}><HeaderCell label="S.NO" /></View>
                    <View style={{ flex: col.name, minWidth: 0 }}><HeaderCell label="Candidate" /></View>
                    {col.source > 0 && <View style={{ flex: col.source, minWidth: 0 }}><HeaderCell label="Source" /></View>}
                    <View style={{ flex: col.location, minWidth: 0 }}><HeaderCell label="Location" /></View>
                    <View style={{ flex: col.exp, minWidth: 0 }}><HeaderCell label="Exp" /></View>
                    <View style={{ flex: col.resume, minWidth: 0 }}><HeaderCell label="Resume" /></View>
                  </View>
                  <View style={{ width: col.actions }} />
                </View>
                {shown.map((it, i) => (
                  <Row
                    key={it.id}
                    item={it}
                    index={i}
                    pageOffset={((result?.page ?? 1) - 1) * PAGE_SIZE}
                    deleting={deleting && pendingDelete?.id === it.id}
                    editLoading={editLoadingId === it.id}
                    onOpen={() => openDetail(it.id)}
                    onEdit={() => onEditClick(it)}
                    onDelete={() => setPendingDelete(it)}
                  />
                ))}
              </Panel>
            )}
          </View>
        )}
      </View>

      <AddCandidateV2Modal open={modalOpen} onClose={() => setModalOpen(false)} onDone={onDone} />

      {/* Bulk import — many resumes at once. Refresh the pool on completion so
          the newly created candidates are visible without a manual reload. */}
      <BulkUploadModal
        open={bulkOpen}
        kind="candidate"
        onClose={() => setBulkOpen(false)}
        onDone={() => refresh()}
      />

      {/* Edit → correct contact details (name/email/phone). */}
      <EditCandidatePiiModal
        open={!!editing}
        candidate={editing}
        onClose={() => setEditing(null)}
        onUpdated={() => { setEditing(null); refresh(); }}
      />

      {/* Delete → withdraw consent (DPDP, append-only). */}
      <ConfirmDialog
        visible={!!pendingDelete}
        title="Withdraw consent?"
        message={`${pendingDelete?.name ?? 'This candidate'} will stop being matched and their consent is recorded as withdrawn (DPDP). Nothing is deleted — the record is kept and consent can be renewed later.`}
        confirmText="Withdraw"
        cancelText="Keep active"
        busy={deleting}
        onConfirm={doWithdraw}
        onCancel={() => setPendingDelete(null)}
      />

      {/* Delete → success moment. */}
      <SuccessDialog
        visible={!!withdrawn}
        title="Consent withdrawn"
        message={`${withdrawn ?? 'The candidate'} won't be matched until consent is renewed. Their record and history are preserved.`}
        primaryAction={{ label: 'Done', onPress: () => setWithdrawn(null) }}
        onClose={() => setWithdrawn(null)}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
});
